import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const massTextApiClient = axios.create({
  baseURL: config.api.massTextServiceUrl || 'http://localhost:8015',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

massTextApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(massTextApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(massTextApiClient);

const massTextService = {
  // Group Management
  fetchGroups: async () => {
    try {
      const response = await massTextApiClient.get('mass-text/groups');
      return response.data;
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  },

  createGroup: async ({ group_name, client_ids, opted_out_client_ids }) => {
    try {
      const response = await massTextApiClient.post('mass-text/groups', {
        group_name,
        client_ids,
        opted_out_client_ids: opted_out_client_ids || []
      });
      return response.data;
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  },

  updateGroup: async (group_id, { group_name, client_ids, opted_out_client_ids }) => {
    try {
      const response = await massTextApiClient.put(`mass-text/groups/${group_id}`, {
        group_name,
        client_ids,
        opted_out_client_ids: opted_out_client_ids || []
      });
      return response.data;
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  },

  deleteGroup: async (group_id) => {
    try {
      const response = await massTextApiClient.delete(`mass-text/groups/${group_id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  },

  // Mass Text Management
  fetchMassTexts: async () => {
    try {
      const response = await massTextApiClient.get('mass-text/messages');
      return response.data;
    } catch (error) {
      console.error('Error fetching mass texts:', error);
      throw error;
    }
  },

  createMassText: async ({ message, send_datetime, from_phone_number, client_ids, group_id }) => {
    try {
      const payload = {
        message,
        send_datetime
      };
      
      if (from_phone_number) {
        payload.from_phone_number = from_phone_number;
      }
      
      if (client_ids) {
        payload.client_ids = client_ids;
      }
      
      if (group_id) {
        payload.group_id = group_id;
      }
      
      const response = await massTextApiClient.post('mass-text/messages', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating mass text:', error);
      throw error;
    }
  },

  updateMassText: async (text_id, { message, send_datetime, from_phone_number, client_ids, group_id }) => {
    try {
      const payload = {
        message,
        send_datetime
      };
      
      if (from_phone_number) {
        payload.from_phone_number = from_phone_number;
      }
      
      if (client_ids) {
        payload.client_ids = client_ids;
      }
      
      if (group_id) {
        payload.group_id = group_id;
      }
      
      const response = await massTextApiClient.put(`mass-text/messages/${text_id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating mass text:', error);
      throw error;
    }
  },

  deleteMassText: async (text_id) => {
    try {
      const response = await massTextApiClient.delete(`mass-text/messages/${text_id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting mass text:', error);
      throw error;
    }
  },

  // Send mass text immediately
  sendMassTextNow: async (text_id) => {
    try {
      const response = await massTextApiClient.post(`mass-text/messages/${text_id}/send`);
      return response.data;
    } catch (error) {
      console.error('Error sending mass text now:', error);
      throw error;
    }
  },

  // Phone Number Management
  fetchPhoneNumbers: async () => {
    try {
      const response = await massTextApiClient.get('mass-text/phone-numbers');
      return response.data;
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      throw error;
    }
  }
};

export default massTextService;
